import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'wouter';
import { useSharedTimeline } from '@/hooks/useShare';
import { useLiveState } from '@/hooks/useLiveState';
import { NowCard } from '@/components/live/NowCard';
import { NextCard } from '@/components/live/NextCard';
import { CountdownTimer } from '@/components/live/CountdownTimer';
import { TagBadge } from '@/components/shared/TagBadge';
import { formatTime, formatCountdown } from '@/lib/utils';
import type { WeddingEvent } from '@/lib/types';
import { cn } from '@/lib/utils';

function useCountdownFormatted(target: Date | null): string {
  const [formatted, setFormatted] = useState('');

  useEffect(() => {
    if (!target) {
      setFormatted('');
      return;
    }
    const update = () => {
      const diff = Math.max(0, Math.floor((target.getTime() - Date.now()) / 1000));
      setFormatted(formatCountdown(diff));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [target]);

  return formatted;
}

function NowNextSection({ events }: { events: WeddingEvent[] }) {
  // Pass a no-op callback since we don't do status mutations in the read-only view
  const noopTransition = useCallback(() => {}, []);
  const liveState = useLiveState(events, noopTransition);

  const countdownFormatted = useCountdownFormatted(liveState.countdownTarget);

  const countdownLabel = liveState.nowEvent
    ? `until end of ${liveState.nowEvent.name}`
    : liveState.nextEvent
      ? `until ${liveState.nextEvent.name}`
      : '';

  return (
    <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {countdownFormatted && countdownLabel && (
        <CountdownTimer formatted={countdownFormatted} label={countdownLabel} />
      )}
      <NowCard
        event={liveState.nowEvent}
        progressPercent={liveState.progressPercent}
        isRunningOver={liveState.isRunningOver}
        timeRemaining={countdownFormatted}
      />
      <NextCard event={liveState.nextEvent} minutesUntil={liveState.minutesUntilNext} />
    </div>
  );
}

export function SharedViewPage() {
  const { shareId } = useParams<{ shareId: string }>();

  const [sharePin, setSharePin] = useState<string | undefined>(undefined);
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '']);
  const [pinError, setPinError] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);

  const { data, error, isLoading } = useSharedTimeline(shareId ?? '', sharePin);

  const typedError = error as (Error & { pinRequired?: boolean }) | null;
  const isPinRequired = !!typedError?.pinRequired;
  const isNotFound = !!error && !isPinRequired;

  // When a wrong PIN causes a 401, trigger the shake + reset
  useEffect(() => {
    if (isPinRequired && sharePin !== undefined) {
      // We had a pin but still got 401 — wrong pin
      setPinError(true);
      const timer = setTimeout(() => {
        setPinDigits(['', '', '', '']);
        setSharePin(undefined);
        setPinError(false);
        // Focus first input after reset
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isPinRequired, sharePin]);

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      const digit = value.replace(/\D/g, '').slice(-1);
      const next = [...pinDigits];
      next[index] = digit;
      setPinDigits(next);

      // Auto-advance
      if (digit && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when 4th digit is entered
      if (index === 3 && digit) {
        const fullPin = [...next.slice(0, 3), digit].join('');
        if (fullPin.length === 4) {
          setSharePin(fullPin);
        }
      }
    },
    [pinDigits]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [pinDigits]
  );

  // Loading state
  if (isLoading && !data) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-background)',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '4px solid var(--color-primary)',
            borderTopColor: 'transparent',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: 0 }}>
          Loading timeline…
        </p>
      </div>
    );
  }

  // PIN gate
  if (isPinRequired) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-background)',
        }}
      >
        <div
          style={{
            maxWidth: 320,
            width: 'calc(100% - 32px)',
            background: 'var(--color-surface)',
            borderRadius: 24,
            padding: 24,
            border: '1px solid var(--color-border)',
          }}
        >
          <p
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--color-text)',
              margin: 0,
              textAlign: 'center',
            }}
          >
            Enter PIN
          </p>
          <p
            style={{
              fontSize: 13,
              color: 'var(--color-text-muted)',
              margin: '4px 0 0 0',
              textAlign: 'center',
            }}
          >
            This timeline is PIN protected
          </p>

          {/* PIN inputs */}
          <div
            className={cn(pinError && 'animate-shake')}
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              marginTop: 24,
            }}
          >
            {pinDigits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  border: `1px solid ${pinError ? '#EF4444' : 'rgba(255,255,255,0.2)'}`,
                  background: 'var(--color-background)',
                  color: 'var(--color-text)',
                  fontSize: 20,
                  textAlign: 'center',
                  fontFamily: 'var(--font-mono)',
                  outline: 'none',
                  transition: 'border-color 150ms ease',
                }}
              />
            ))}
          </div>

          {pinError && (
            <p
              style={{
                fontSize: 13,
                color: '#EF4444',
                textAlign: 'center',
                marginTop: 8,
              }}
            >
              Incorrect PIN
            </p>
          )}
        </div>
      </div>
    );
  }

  // Error state (not pin-related)
  if (isNotFound) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-background)',
          gap: 16,
        }}
      >
        <p style={{ fontSize: 16, color: 'var(--color-text-muted)', textAlign: 'center', margin: 0 }}>
          This timeline is no longer available.
        </p>
        <p
          style={{
            position: 'absolute',
            bottom: 24,
            fontSize: 12,
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            margin: 0,
          }}
        >
          Powered by WedTrack
        </p>
      </div>
    );
  }

  // Ready state
  if (!data) return null;

  const { wedding, events } = data;

  const formattedDate = wedding.weddingDate
    ? new Date(wedding.weddingDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-background)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sticky header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          height: 56,
          background: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--color-text)',
          }}
        >
          {wedding.coupleNames ?? 'Wedding Timeline'}
        </span>
        {formattedDate && (
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {formattedDate}
          </span>
        )}
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, paddingBottom: 64 }}>
        <NowNextSection events={events} />

        {/* Full event list */}
        <div style={{ marginTop: 16, paddingLeft: 16, paddingRight: 16 }}>
          <p
            className="text-xs uppercase tracking-widest mb-3"
            style={{ color: 'var(--color-text-muted)', letterSpacing: '1.5px', margin: 0, marginBottom: 12 }}
          >
            Full Schedule
          </p>
          {events.map((event) => (
            <div
              key={event.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingTop: 12,
                paddingBottom: 12,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                gap: 12,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                  width: 56,
                  flexShrink: 0,
                  color: 'var(--color-text-muted)',
                }}
              >
                {formatTime(event.startTime)}
              </span>
              <span style={{ flex: 1, fontSize: 14, color: 'var(--color-text)' }}>
                {event.name}
              </span>
              <TagBadge category={event.category} />
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>
          Powered by WedTrack
        </p>
      </div>
    </div>
  );
}
